import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_EVENTS_QUERY, processEvents, combineAndSortEvents } from '../../../utils/eventUtils';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Hero from '../../common/Hero';
import ImageSlider from '../../common/Image-slider';
import WhatsOn from '../../common/What-on';
import NewsletterSignUp from '../../common/Newsletter-sign';
import ImageSlant from '../../common/image-slant';
import Footer from '../../Footer/Footer';
import Button from '../../Button/Button';
import imgFestive from '../../../assets/christmas25/festive.webp';
import imgTea1 from '../../../assets/christmas25/tea1.webp';
import imgTea2 from '../../../assets/christmas25/tea2.webp';
import imgGrotto from '../../../assets/christmas25/grotto.webp';
import retailImage from '../../../assets/christmas-retail-village.png';
import ChristmasAboutModal from '../../common/ChristmasAboutModal';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const christmasHeroImage = 'https://BelvoirCastlePull.b-cdn.net/Website/Belvoir%20Castle%20Christmas-94.webp';

const imgCandlelight = 'https://www.belvoircastle.com/wp/wp-content/uploads/2025/11/Castle-by-Candlelight.webp';

const imgSearch = 'https://www.belvoircastle.com/wp/wp-content/uploads/2026/07/Belvoir-Castle-Christmas-114-scaled.webp';

const C = {
  navy: '#101d40',
  midnight: '#0A1428',
  gold: '#C9963C',
  goldDk: '#A67C2A',
  ivory: '#FAF7F0',
  parchment: '#F3EDD8',
  muted: 'rgba(28,28,30,0.62)',
  border: 'rgba(28,28,30,0.08)',
};

const BOOKING_URL = '/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761';

const Container = styled.div`
  min-height: 100vh;
  padding-top: 158px;
  background: ${C.ivory};
`;

const HeroSpacer = styled.div`
  height: 240px;
  @media (max-width: 1024px) { height: 200px; }
  @media (max-width: 768px) { height: 0; }
`;

const DeckHallSection = styled.section`
  border-top: 1px solid ${C.border};
`;

const DeckHallBlock = styled.div`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || C.ivory};
  border-bottom: ${p => p.$border ? `1px solid ${C.border}` : 'none'};
`;

const DeckHallInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const DeckHallKicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
  text-align: ${p => p.$center ? 'center' : 'left'};
`;

const DeckHallDivider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.gold};
  margin: ${p => p.$center ? '0 auto clamp(1.1rem, 2.5vw, 1.6rem)' : '0 0 clamp(1.1rem, 2.5vw, 1.6rem)'};
`;

const DeckHallTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.9rem, 4.2vw, 2.85rem);
  font-weight: 700;
  line-height: 1.1;
  color: ${p => p.$light ? '#fff' : C.navy};
  text-align: ${p => p.$center ? 'center' : 'left'};
  em { font-style: italic; color: ${C.gold}; }
`;

const DeckHallLead = styled.p`
  max-width: ${p => p.$wide ? '720px' : '640px'};
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${p => p.$light ? 'rgba(255,255,255,0.62)' : C.muted};
  margin: 0 0 ${p => p.$last ? '0' : 'clamp(1rem, 2vw, 1.35rem)'};
  text-align: ${p => p.$center ? 'center' : 'left'};
  ${p => p.$center && 'margin-left: auto; margin-right: auto;'}
`;

const DeckHallTwoCols = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1.05fr 0.95fr'};
  gap: clamp(2rem, 5vw, 4rem);
  align-items: center;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const DeckHallImage = styled.div`
  border-radius: 14px;
  overflow: hidden;
  height: clamp(280px, 38vw, 460px);
  background: ${C.navy};
  box-shadow: 0 20px 60px rgba(16, 29, 64, 0.14);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const DeckHallInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: ${C.border};
  border-radius: 10px;
  overflow: hidden;
  margin-top: clamp(2rem, 4vw, 3rem);

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const DeckHallInfoItem = styled.div`
  background: #fff;
  padding: clamp(1.2rem, 2.5vw, 1.65rem);
`;

const DeckHallInfoLabel = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.38);
  margin-bottom: 0.35rem;
`;

const DeckHallInfoValue = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1rem, 2vw, 1.15rem);
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const DeckHallFeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(1rem, 2vw, 1.4rem);
  margin-top: clamp(2rem, 4vw, 3rem);

  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const DeckHallFeatureCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: clamp(1.5rem, 3vw, 2rem);
  border: 1px solid ${C.border};
  box-shadow: 0 4px 20px rgba(16, 29, 64, 0.06);
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DeckHallFeatureAccent = styled.div`
  width: 44px;
  height: 3px;
  background: ${C.gold};
  border-radius: 2px;
  margin-bottom: 1rem;
`;

const DeckHallFeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${C.navy};
`;

const DeckHallFeatureBody = styled.p`
  margin: 0 0 1.25rem;
  flex: 1;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.65;
  color: ${C.muted};
`;

const DeckHallFeatureLink = styled(Link)`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${C.gold};
  text-decoration: none;
  transition: color 0.2s ease;
  &:hover { color: ${C.goldDk}; }
`;

const DeckHallHighlightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(0.85rem, 2vw, 1.1rem);
  margin-top: clamp(2rem, 4vw, 2.75rem);

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const DeckHallHighlightItem = styled.div`
  padding: 1.1rem 1.15rem 1.15rem 1rem;
  border-left: 3px solid ${C.gold};
  background: rgba(255, 255, 255, 0.04);
  border-radius: 0 8px 8px 0;
`;

const DeckHallHighlightText = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.875rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.72);
`;

const DeckHallCtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  margin-top: clamp(2rem, 4vw, 2.75rem);
`;

const DeckHallPrimaryBtn = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1rem 2.25rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.18s ease;
  &:hover { background: ${C.goldDk}; transform: translateY(-1px); }
`;

const DeckHallSecondaryBtn = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255, 255, 255, 0.35);
  background: transparent;
  color: #fff;
  text-decoration: none;
  transition: border-color 0.2s ease, background 0.2s ease;
  &:hover {
    border-color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.06);
  }
`;

const Card = styled.div.attrs({ className: 'bf-surface' })`
  border-radius: 16px;
  padding: clamp(1.5rem, 3vw, 2rem);
  margin-bottom: 1.25rem;
  border: 1px solid rgba(16, 29, 64, 0.09);
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  box-shadow:
    0 8px 32px rgba(16, 29, 64, 0.07),
    0 1px 0 rgba(255, 255, 255, 0.88) inset;
`;

const FamilySaverSection = styled.section`
  position: relative;
  width: 100%;
  margin: 0;
  padding: clamp(3.5rem, 8vw, 5.5rem) clamp(1rem, 4vw, 2rem);
  background: #080f20;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at 15% 60%, rgba(201, 150, 60, 0.14) 0%, transparent 55%),
      radial-gradient(ellipse at 85% 10%, rgba(16, 29, 64, 0.6) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const FamilySaverInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1140px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(2.5rem, 6vw, 5rem);
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const FamilySaverLeft = styled.div``;

const FamilySaverBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(201, 150, 60, 0.45);
  color: ${C.gold};
  padding: 0.4rem 1rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin-bottom: 1.35rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    font-size: 0.65rem;
  }

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    background: ${C.gold};
    border-radius: 50%;
    display: block;
    flex-shrink: 0;
  }
`;

const FamilySaverHeading = styled.h2`
  font-size: clamp(1.9rem, 4vw, 2.8rem);
  margin: 0 0 1.1rem;
  font-family: ${props => props.theme.fonts.main};
  letter-spacing: 0.01em;
  line-height: 1.1;
  color: #fff;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: clamp(1.65rem, 5.5vw, 2.1rem);
  }
`;

const FamilySaverLead = styled.p`
  font-size: clamp(0.98rem, 1.8vw, 1.08rem);
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.62);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  margin: 0 0 1.85rem;
  max-width: 30rem;

  @media (max-width: 900px) {
    max-width: none;
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const FamilySaverBenefits = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const FamilySaverBenefit = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.78);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const BenefitCheck = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(201, 150, 60, 0.18);
  border: 1px solid rgba(201, 150, 60, 0.5);
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  color: ${C.gold};
  font-weight: 700;
  line-height: 1;
`;

const FamilySaverRight = styled.div``;

const FamilySaverCard = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 20px;
  padding: clamp(1.75rem, 4vw, 2.5rem);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 15%;
    right: 15%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(201, 150, 60, 0.65),
      transparent
    );
  }
`;

const FamilySaverCardEyebrow = styled.p`
  font-size: 0.6875rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${C.gold};
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 700;
  margin: 0 0 0.55rem;
`;

const FamilySaverCardHeadline = styled.h3`
  font-size: clamp(1.25rem, 2.5vw, 1.6rem);
  color: #fff;
  font-family: ${props => props.theme.fonts.main};
  font-weight: 700;
  margin: 0 0 1.75rem;
  line-height: 1.2;
`;

const TierList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const TierRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.15rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-left: 2px solid rgba(201, 150, 60, 0.45);
  border-radius: 12px;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-left-color: rgba(201, 150, 60, 0.75);
  }
`;

const TierLabel = styled.div`
  flex: 1;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9375rem;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const TierBadge = styled.div`
  background: rgba(201, 150, 60, 0.18);
  border: 1px solid rgba(201, 150, 60, 0.38);
  color: ${C.gold};
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  text-transform: uppercase;
  white-space: nowrap;
`;

const FamilySaverCTA = styled.div`
  display: flex;

  button {
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.28);
    transition:
      opacity 0.2s ease,
      transform 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 8px 26px rgba(0, 0, 0, 0.32);
    }
  }
`;


const ChristmasHome = () => {
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const { data: allEventsData } = useQuery(GET_EVENTS_QUERY);
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const familySaverBadgeText = isBlackFriday ? 'BLACK FRIDAY' : 'CHRISTMAS 2026';
  const familySaverHeadingText = isBlackFriday ? 'Black Friday: 20% Off Entry and Santa Tickets + Additional Family Saving' : 'Opening Our Doors Wider Than Ever';
  const familySaverButtonLabel = isBlackFriday ? 'Unlock 20% Off' : 'Discover Your Family Savings';
  const familySaverSubheadingText = isBlackFriday
    ? "Secure your Christmas visit and unlock an automatic 20% saving on Entry and Santa Tickets when you build your Family Saver party."
    : "This Christmas, we're delighted to welcome you back to our revolutionary Family Saver booking system";
  
  // Hardcoded Christmas events — only these appear on the Christmas home page
  const hardcodedChristmasEvents = [
    {
      id: null,
      title: "Santa's Grotto — Belvoir Castle",
      date: 'Saturday, November 14, 2026 to Wednesday, December 30, 2026',
      description: "Meet Santa in his magical grotto nestled inside the heart of Belvoir Castle. A private visit with Father Christmas during Deck the Halls — every child receives a special present.",
      image: imgGrotto,
      location: 'Castle',
      link: '/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761',
      linkText: 'Book Now',
      isFlagship: true,
      eventTags: ['christmas', 'family', 'santa'],
      eventDetails: { eventDate: '2026-11-14', eventEndDate: '2026-12-30' }
    },
    {
      id: null,
      title: "Santa's Grotto — Retail Village",
      date: 'Saturday, November 14, 2026 to Wednesday, December 30, 2026',
      description: "Meet Santa in his grotto at Belvoir Retail Village. A heartwarming family visit with a present for every child — no Castle entry ticket required.",
      image: imgGrotto,
      location: 'Retail Village',
      link: '/booking-journey?locationId=978&pageId=d4314a7f-aa55-4e17-994e-f58655da7072',
      linkText: 'Book Now',
      isFlagship: true,
      eventTags: ['christmas', 'family', 'santa'],
      eventDetails: { eventDate: '2026-11-14', eventEndDate: '2026-12-30' }
    },
    {
      id: null,
      title: 'Search for Santa',
      date: '5, 6, 12, 13, 19, 20, 21 & 23 December 2026, 5:00 PM',
      description: 'Exclusive late afternoon family trail through the Castle to find Santa. Children enjoy hot chocolate, a biscuit and a present; parents get refreshments and a glass of bubbles. Full-day Castle access included.',
      image: imgSearch,
      location: 'Castle',
      link: '/booking-journey?locationId=978&pageId=d771a85f-632e-4a25-8651-c3a60fb31a10',
      linkText: 'Book Now',
      isFlagship: true,
      eventTags: ['christmas', 'family', 'trail'],
      eventDetails: { eventDate: '2026-12-05T17:00:00', eventEndDate: '2026-12-23T19:30:00' }
    },
    {
      id: null,
      title: 'Festive Mice Trail',
      date: 'Saturday, November 14, 2026 to Wednesday, December 30, 2026',
      description: 'Follow the beloved Mice Trail through the Castle\'s carol-themed grand rooms. Included with Entry.',
      image: imgFestive,
      location: 'Castle',
      link: '/christmas/mice-trail',
      linkText: 'Explore the Mice Trail',
      eventTags: ['christmas', 'family', 'included'],
      eventDetails: { eventDate: '2026-11-14', eventEndDate: '2026-12-30' }
    },
    {
      id: null,
      title: 'Deck The Halls — Belvoir Castle by Candlelight',
      date: 'Thursday, December 10, 2026 and Thursday, December 17, 2026, 6:00 PM',
      description: 'The Christmas Experience by Candlelight with live musicians. Bubbles and a mince pie on arrival in the Guard Room. Free-flow access to the Castle. £40 per person (includes mince pie and glass of fizz).',
      image: imgCandlelight,
      location: 'Castle',
      link: '/christmas/castle-by-candlelight',
      linkText: 'Explore Castle by Candlelight',
      isFlagship: true,
      eventTags: ['christmas', 'limited', 'evening'],
      eventDetails: { eventDate: '2026-12-10T18:00:00', eventEndDate: '2026-12-17T21:00:00' }
    }
  ];
  
  // Only show hardcoded Christmas events — no WordPress events on this page
  const allEvents = hardcodedChristmasEvents;
  
  const slides = [
    { image: imgFestive, title: 'Your Christmas Visit', action: () => window.location.assign('/christmas/your-festive-visit') },
    { image: imgCandlelight, title: 'Castle by Candlelight', action: () => window.location.assign('/christmas/castle-by-candlelight') },
    { image: imgTea1, title: 'Festive Afternoon Tea', action: () => window.location.assign('/christmas/festive-afternoon-tea') },
    { image: imgGrotto, title: "Santa's Grotto", action: () => window.location.assign('/christmas/santas-grotto-options') },
    { image: imgSearch, title: 'Search for Santa', action: () => window.location.assign('/christmas/search-for-santa') },
    { image: retailImage, title: 'Christmas Shopping', action: () => window.location.assign('https://www.belvoircastle.com/retail-village') },
  ];

  // Christmas events data for structured data
  const christmasEvents = [
    {
      "@type": "Event",
      "name": "Christmas at Belvoir Castle 2026",
      "description": "Experience a magical Christmas at Belvoir Castle with Deck the Halls carol-themed rooms, the Mice Trail, Santa's Grotto, afternoon tea, and family activities in Leicestershire.",
      "startDate": "2026-11-14",
      "endDate": "2026-12-30",
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "location": {
        "@type": "Place",
        "name": "Belvoir Castle",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Belvoir Castle",
          "addressLocality": "Grantham",
          "addressRegion": "Leicestershire",
          "postalCode": "NG32 1PE",
          "addressCountry": "GB"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 52.8931,
          "longitude": -0.7792
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "Belvoir Castle",
        "url": "https://www.belvoircastle.com"
      },
      "offers": {
        "@type": "Offer",
        "url": "https://www.belvoircastle.com/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761",
        "priceCurrency": "GBP",
        "availability": "https://schema.org/InStock"
      },
      "image": "https://www.belvoircastle.com/christmas25/festive.webp"
    },
    {
      "@type": "Event",
      "name": "Santa's Grotto at Belvoir Retail Village",
      "description": "Meet Santa in his magical grotto at Belvoir Retail Village. A magical moment for families this Christmas.",
      "startDate": "2026-11-14",
      "endDate": "2026-12-30",
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "location": {
        "@type": "Place",
        "name": "Belvoir Castle",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Belvoir Castle",
          "addressLocality": "Grantham",
          "addressRegion": "Leicestershire",
          "postalCode": "NG32 1PE",
          "addressCountry": "GB"
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "Belvoir Castle",
        "url": "https://www.belvoircastle.com"
      },
      "image": "https://www.belvoircastle.com/christmas25/grotto.webp"
    },
    {
      "@type": "Event",
      "name": "Search for Santa - Belvoir Castle",
      "description": "Exclusive late afternoon family trail through the Castle to find Santa. Children enjoy hot chocolate, a biscuit and a present; parents get refreshments and a glass of bubbles. Full-day Castle access included.",
      "startDate": "2026-12-05T17:00:00",
      "endDate": "2026-12-23T19:30:00",
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "location": {
        "@type": "Place",
        "name": "Belvoir Castle",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Belvoir Castle",
          "addressLocality": "Grantham",
          "addressRegion": "Leicestershire",
          "postalCode": "NG32 1PE",
          "addressCountry": "GB"
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "Belvoir Castle",
        "url": "https://www.belvoircastle.com"
      },
      "offers": {
        "@type": "Offer",
        "url": "https://www.belvoircastle.com/booking-journey?locationId=978&pageId=d771a85f-632e-4a25-8651-c3a60fb31a10",
        "availability": "https://schema.org/InStock"
      },
      "image": "https://www.belvoircastle.com/wp/wp-content/uploads/2026/07/Belvoir-Castle-Christmas-114-scaled.webp"
    },
    {
      "@type": "Event",
      "name": "Deck The Halls — Belvoir Castle by Candlelight",
      "description": "The Christmas Experience by Candlelight with live musicians. Bubbles and a mince pie on arrival in the Guard Room. Free-flow access to the Castle.",
      "startDate": "2026-12-10T18:00:00",
      "endDate": "2026-12-17T21:00:00",
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "location": {
        "@type": "Place",
        "name": "Belvoir Castle",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Belvoir Castle",
          "addressLocality": "Grantham",
          "addressRegion": "Leicestershire",
          "postalCode": "NG32 1PE",
          "addressCountry": "GB"
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "Belvoir Castle",
        "url": "https://www.belvoircastle.com"
      },
      "offers": {
        "@type": "Offer",
        "price": "40",
        "priceCurrency": "GBP",
        "availability": "https://schema.org/LimitedAvailability"
      },
      "image": "https://www.belvoircastle.com/wp/wp-content/uploads/2025/11/Castle-by-Candlelight.webp"
    }
  ];

  // Main organization structured data
  const organizationData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Belvoir Castle",
    "description": "Historic castle offering Christmas events, carol-themed rooms, Mice Trail, Santa's Grotto, festive afternoon tea, and family activities in Leicestershire and Lincolnshire border.",
    "url": "https://www.belvoircastle.com/christmas",
    "isAccessibleForFree": false,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Belvoir Castle",
      "addressLocality": "Grantham",
      "addressRegion": "Leicestershire",
      "postalCode": "NG32 1PE",
      "addressCountry": "UK"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 52.8931,
      "longitude": -0.7792
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday", 
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday"
      ],
      "opens": "10:00",
      "closes": "17:00",
      "validFrom": "2026-11-14",
      "validThrough": "2026-12-30"
    },
    "priceRange": "££",
    "telephone": "+441476871001",
    "email": "info@belvoircastle.com",
    "image": "https://www.belvoircastle.com/christmas25/festive.webp",
    "sameAs": [
      "https://www.facebook.com/belvoircastle",
      "https://www.instagram.com/belvoircastle",
      "https://twitter.com/belvoircastle"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Christmas Events 2026",
      "itemListElement": christmasEvents
    }
  };

  return (
    <Container className={pageClassName}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Christmas at Belvoir Castle 2026 | Deck the Halls — Carol-Themed Rooms</title>
        <meta name="title" content="Christmas at Belvoir Castle 2026 | Deck the Halls — Carol-Themed Rooms" />
        <meta name="description" content="Experience the magic of Christmas 2026 at Belvoir Castle. Deck the Halls — each grand room themed as a different Christmas carol. Mice Trail, Santa's Grotto, Search for Santa & family activities in Leicestershire. Book now!" />
        <meta name="keywords" content="Christmas at Belvoir Castle, Christmas events Leicestershire, Deck the Halls, carol-themed rooms, Santa's Grotto, Mice Trail, Christmas afternoon tea, Christmas family activities, Christmas castle events, festive events Lincolnshire, Christmas day out Leicestershire, Christmas activities near me, Belvoir Castle Christmas 2026" />
        <meta name="author" content="Belvoir Castle" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="geo.region" content="GB-LEI" />
        <meta name="geo.placename" content="Grantham, Leicestershire" />
        <meta name="geo.position" content="52.8931;-0.7792" />
        <meta name="ICBM" content="52.8931, -0.7792" />
        <link rel="canonical" href="https://www.belvoircastle.com/christmas" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/christmas" />
        <meta property="og:title" content="Christmas at Belvoir Castle 2026 | Deck the Halls — Carol-Themed Rooms" />
        <meta property="og:description" content="Experience magical Christmas 2026 at Belvoir Castle. Deck the Halls with carol-themed rooms, the Mice Trail, Santa's Grotto & family activities in Leicestershire. Book your Christmas visit today!" />
        <meta property="og:image" content="https://www.belvoircastle.com/christmas25/festive.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.belvoircastle.com/christmas" />
        <meta name="twitter:title" content="Christmas at Belvoir Castle 2026 | Deck the Halls — Carol-Themed Rooms" />
        <meta name="twitter:description" content="Experience magical Christmas 2026 at Belvoir Castle. Deck the Halls with carol-themed rooms, the Mice Trail, Santa's Grotto & family activities in Leicestershire." />
        <meta name="twitter:image" content="https://www.belvoircastle.com/christmas25/festive.webp" />
        <meta name="twitter:site" content="@belvoircastle" />
        <meta name="twitter:image:alt" content="Christmas at Belvoir Castle 2026" />

        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content="#101d40" />
        <meta name="msapplication-TileColor" content="#101d40" />
        <meta name="apple-mobile-web-app-title" content="Christmas at Belvoir Castle" />
        <meta name="application-name" content="Belvoir Castle Christmas" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(organizationData)}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.belvoircastle.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Christmas 2026",
                "item": "https://www.belvoircastle.com/christmas"
              }
            ]
          })}
        </script>
      </Helmet>
      
      <Hero 
        backgroundImage={christmasHeroImage}
        welcomeText="This Christmas at"
        mainTitle="Belvoir Castle"
        subtitle="Deck the Halls"
        darkOverlay={isBlackFriday}
        buttons={[
          { text: 'Book Now', onClick: () => window.location.assign('/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761') },
          { text: 'Your Christmas Visit', onClick: () => window.location.assign('/christmas/your-festive-visit') }
        ]}
        infoColumns={[
          { title: 'Opening Times', items: ['14 November - 30 December', '10am - 5pm'] },
          { 
            title: 'Afternoon Tea', 
            items: [
              'Seasonal menus', 
              { text: 'Festive Afternoon Tea', link: '/christmas/festive-afternoon-tea' }
            ] 
          },
          { 
            title: 'Mice Trail', 
            items: [
              'Included with Entry', 
              { text: 'Carol-themed rooms', link: '/christmas/mice-trail' }
            ] 
          },
          { 
            title: "Meet Santa", 
            items: [
              { text: "Santa's Grotto", link: '/christmas/santas-grotto-options' }, 
              { text: 'Search for Santa', link: '/christmas/search-for-santa' }
            ] 
          },
        
        ]}
      />

      <ImageSlider slides={slides} darkMode={isBlackFriday} />
      
      <FamilySaverSection>
        <FamilySaverInner>
          <FamilySaverLeft>
            <FamilySaverBadge>{familySaverBadgeText}</FamilySaverBadge>
            <FamilySaverHeading>{familySaverHeadingText}</FamilySaverHeading>
            <FamilySaverLead>
              {isBlackFriday
                ? familySaverSubheadingText
                : "We believe Christmas magic should be shared with everyone you love. Our Family Saver system rewards families of all sizes — simply build your party and watch the savings grow."}
            </FamilySaverLead>
            <FamilySaverBenefits>
              <FamilySaverBenefit>
                <BenefitCheck>✓</BenefitCheck>
                Savings stack automatically as you add to your group
              </FamilySaverBenefit>
              <FamilySaverBenefit>
                <BenefitCheck>✓</BenefitCheck>
                Add children, grandparents, friends — everyone is welcome
              </FamilySaverBenefit>
              <FamilySaverBenefit>
                <BenefitCheck>✓</BenefitCheck>
                {isBlackFriday ? '20% off Entry and Santa Tickets during Black Friday' : 'The bigger your party, the greater your saving'}
              </FamilySaverBenefit>
            </FamilySaverBenefits>
            <FamilySaverCTA>
              <Button onClick={() => window.location.assign('/christmas/tickets')}>
                {familySaverButtonLabel}
              </Button>
            </FamilySaverCTA>
          </FamilySaverLeft>

          <FamilySaverRight>
            <FamilySaverCard>
              <FamilySaverCardEyebrow>Family Saver</FamilySaverCardEyebrow>
              <FamilySaverCardHeadline>
                The more you add,<br />the more you save
              </FamilySaverCardHeadline>
              <TierList>
                <TierRow>
                  <TierLabel>A couple or pair</TierLabel>
                  <TierBadge>Save more</TierBadge>
                </TierRow>
                <TierRow>
                  <TierLabel>A family of four</TierLabel>
                  <TierBadge>Save more</TierBadge>
                </TierRow>
                <TierRow>
                  <TierLabel>A group of six or more</TierLabel>
                  <TierBadge>Best saving</TierBadge>
                </TierRow>
              </TierList>
              <Button onClick={() => window.location.assign('/christmas/tickets')}>
                Build Your Group
              </Button>
            </FamilySaverCard>
          </FamilySaverRight>
        </FamilySaverInner>
      </FamilySaverSection>

      <DeckHallSection>
        <DeckHallBlock $bg={C.ivory} $border>
          <DeckHallInner>
            <DeckHallTwoCols>
              <div>
                <DeckHallKicker>Christmas at Belvoir Castle</DeckHallKicker>
                <DeckHallDivider />
                <DeckHallTitle>
                  Deck the Halls — Your <em>Christmas 2026</em>
                </DeckHallTitle>
                <DeckHallLead>
                  This Christmas, Belvoir Castle transforms into a world of song and story. Our grand rooms are dressed in the spirit of beloved Christmas carols — from Deck the Halls to The Twelve Days of Christmas — filling every corridor with wonder.
                </DeckHallLead>
                <DeckHallLead $last>
                  Follow the Mice Trail through the Castle, meet Father Christmas, enjoy festive afternoon tea, or experience the magic of Castle by Candlelight. A Christmas visit to remember.
                </DeckHallLead>
              </div>
              <DeckHallImage>
                <img src={imgFestive} alt="Deck the Halls carol-themed rooms at Belvoir Castle" />
              </DeckHallImage>
            </DeckHallTwoCols>

            <DeckHallInfoGrid>
              <DeckHallInfoItem>
                <DeckHallInfoLabel>Opening Dates</DeckHallInfoLabel>
                <DeckHallInfoValue>14 Nov – 30 Dec 2026</DeckHallInfoValue>
              </DeckHallInfoItem>
              <DeckHallInfoItem>
                <DeckHallInfoLabel>Opening Hours</DeckHallInfoLabel>
                <DeckHallInfoValue>10am – 5pm daily</DeckHallInfoValue>
              </DeckHallInfoItem>
              <DeckHallInfoItem>
                <DeckHallInfoLabel>Mice Trail</DeckHallInfoLabel>
                <DeckHallInfoValue>Included with Entry</DeckHallInfoValue>
              </DeckHallInfoItem>
              <DeckHallInfoItem>
                <DeckHallInfoLabel>Castle by Candlelight</DeckHallInfoLabel>
                <DeckHallInfoValue>Super early bird prices available</DeckHallInfoValue>
              </DeckHallInfoItem>
            </DeckHallInfoGrid>
          </DeckHallInner>
        </DeckHallBlock>

        <DeckHallBlock $bg="#fff" $border>
          <DeckHallInner>
            <DeckHallKicker $center>The Experience</DeckHallKicker>
            <DeckHallDivider $center />
            <DeckHallTitle $center>
              Three Unforgettable <em>Experiences</em>
            </DeckHallTitle>
            <DeckHallLead $center $wide $last>
              From carol-themed rooms to Santa encounters — discover everything that awaits you at Belvoir Castle this Christmas.
            </DeckHallLead>

            <DeckHallFeatureGrid>
              <DeckHallFeatureCard>
                <DeckHallFeatureAccent />
                <DeckHallFeatureTitle>Carol-Themed Rooms</DeckHallFeatureTitle>
                <DeckHallFeatureBody>
                  Each of Belvoir Castle's grand rooms is dressed in the spirit of a beloved Christmas carol — filling every corridor with song, story and wonder. A first for Belvoir Castle.
                </DeckHallFeatureBody>
                <DeckHallFeatureLink to="/christmas/mice-trail">Explore the Mice Trail</DeckHallFeatureLink>
              </DeckHallFeatureCard>

              <DeckHallFeatureCard>
                <DeckHallFeatureAccent />
                <DeckHallFeatureTitle>The Mice Trail Returns</DeckHallFeatureTitle>
                <DeckHallFeatureBody>
                  Our much-loved Mice Trail is back for 2026. Follow the Three Mice through the Castle's carol-themed rooms on an interactive adventure for children. Included with Entry — no extra booking required.
                </DeckHallFeatureBody>
                <DeckHallFeatureLink to="/christmas/mice-trail">Discover the Trail</DeckHallFeatureLink>
              </DeckHallFeatureCard>

              <DeckHallFeatureCard>
                <DeckHallFeatureAccent />
                <DeckHallFeatureTitle>Meet Father Christmas</DeckHallFeatureTitle>
                <DeckHallFeatureBody>
                  Visit Santa's Grotto at the Retail Village, or join the exclusive Search for Santa trail through the Castle. Two distinct experiences, each designed to create lasting family memories.
                </DeckHallFeatureBody>
                <DeckHallFeatureLink to="/christmas/santas-grotto-options">Explore Santa Experiences</DeckHallFeatureLink>
              </DeckHallFeatureCard>
            </DeckHallFeatureGrid>
          </DeckHallInner>
        </DeckHallBlock>

        <DeckHallBlock $bg={C.midnight}>
          <DeckHallInner>
            <DeckHallKicker $center>What's Included</DeckHallKicker>
            <DeckHallDivider $center />
            <DeckHallTitle $center $light>
              Everything Awaiting You <em>This Christmas</em>
            </DeckHallTitle>

            <DeckHallHighlightGrid>
              <DeckHallHighlightItem>
                <DeckHallHighlightText>Each grand room dressed as a different Christmas carol — a first for Belvoir Castle</DeckHallHighlightText>
              </DeckHallHighlightItem>
              <DeckHallHighlightItem>
                <DeckHallHighlightText>The beloved Mice Trail returns, winding through the carol-themed rooms</DeckHallHighlightText>
              </DeckHallHighlightItem>
              <DeckHallHighlightItem>
                <DeckHallHighlightText>Deck The Halls by Candlelight — live musicians, bubbles and mince pie on arrival, £40 per person</DeckHallHighlightText>
              </DeckHallHighlightItem>
              <DeckHallHighlightItem>
                <DeckHallHighlightText>Festive Afternoon Tea featuring indulgent Christmas treats and winter flavours</DeckHallHighlightText>
              </DeckHallHighlightItem>
              <DeckHallHighlightItem>
                <DeckHallHighlightText>Santa's Grotto at the Retail Village and Search for Santa at the Castle</DeckHallHighlightText>
              </DeckHallHighlightItem>
              <DeckHallHighlightItem>
                <DeckHallHighlightText>Beautiful Christmas parkland and grounds perfect for winter walks</DeckHallHighlightText>
              </DeckHallHighlightItem>
              <DeckHallHighlightItem>
                <DeckHallHighlightText>Christmas shopping and festive gifts at the Belvoir Retail Village</DeckHallHighlightText>
              </DeckHallHighlightItem>
            </DeckHallHighlightGrid>

            <DeckHallCtaRow>
              <DeckHallPrimaryBtn href={BOOKING_URL}>
                Book Your Christmas Visit
              </DeckHallPrimaryBtn>
              <DeckHallSecondaryBtn to="/christmas/your-festive-visit">
                Plan Your Visit
              </DeckHallSecondaryBtn>
            </DeckHallCtaRow>
          </DeckHallInner>
        </DeckHallBlock>
      </DeckHallSection>

      <WhatsOn 
        title="Christmas Events & Activities at Belvoir Castle 2026"
        enableScrolling={true}
        events={allEvents}
        showParklandSection={false}
        darkMode={isBlackFriday}
      />
      
      <NewsletterSignUp tag="Christmas 2026" />

      <ImageSlant 
        image={imgTea2}
        title="Christmas Afternoon Tea at Belvoir Castle"
        description="Indulge in a festive selection of Christmas sweet and savoury treats in our elegant castle tearooms, surrounded by the magic of our Deck the Halls carol-themed rooms."
        buttonText="Book Christmas Afternoon Tea"
        buttonAction={() => window.location.assign('/christmas/festive-afternoon-tea')}
        darkMode={isBlackFriday}
        imagePosition="left"
      />

      <ImageSlant 
        image={imgGrotto}
        title="Meet Santa at Belvoir Castle Christmas"
        description="Create magical Christmas memories with a visit to Santa's Grotto at the Retail Village, or join the Search for Santa trail through our Deck the Halls castle rooms."
        buttonText="Explore Christmas Santa Experiences"
        buttonAction={() => window.location.assign('/christmas/santas-grotto-options')}
        darkMode={isBlackFriday}
        imagePosition="right"
      />
      <Footer />
      <ChristmasAboutModal 
        isOpen={isAboutModalOpen} 
        onClose={() => setIsAboutModalOpen(false)} 
      />
    </Container>
  );
};

export default ChristmasHome;

